/**
 * useBusinessConversion
 *
 * The SCI "business customer" UI model: everything Convert-to-Business needs, so `useSCICustomerCard`
 * can compose it and the component stays free of this logic. Reads the buyer/deal signals that gate the
 * entry CTA, derives the CTA/search-step view flags, and exposes the start/continue/cancel handlers
 * (which delegate to the card's imperative ref). The single `cardRef` and the operation live in the
 * card's host state owned by `useSCICustomerCard`, so they are passed in rather than owned here.
 */
import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { CARD_OPERATION_STEP } from '@makemydeal/dr-activities-customer-card';
import { getActiveDealCommonOrgId, getIsDealSoldOrContracted, useIsBusinessBuyer } from '@makemydeal/dr-sales-checkin-store';

import type { UseBusinessConversionOptions, UseBusinessConversionResult } from '../types';

export const useBusinessConversion = ({ cardRef, operation }: UseBusinessConversionOptions): UseBusinessConversionResult => {
    const enableBusinessConversion = true;
    const isBuyerBusiness = useIsBusinessBuyer();
    const isDealSoldOrContracted = useSelector(getIsDealSoldOrContracted);
    // Business search needs the org id (the activity store is only seeded with it when present), so
    // hide the CTA without one rather than fire a search with an empty dealershipId.
    const hasCommonOrgId = Boolean(useSelector(getActiveDealCommonOrgId));

    const showConvertToBusinessCta =
        enableBusinessConversion && !operation && !isBuyerBusiness && !isDealSoldOrContracted && hasCommonOrgId;
    const isBusinessSearchStep = operation?.step === CARD_OPERATION_STEP.SEARCH;

    // The wrapper here is specific to business conversion, so keeping the business names same.
    const onConvertToBusiness = useCallback(() => cardRef.current?.convertToBusiness?.(), [cardRef]);
    const onContinueBusinessConversion = useCallback(() => cardRef.current?.continueOperation?.(), [cardRef]);
    const onCancelBusinessConversion = useCallback(() => cardRef.current?.cancelOperation?.(), [cardRef]);

    return {
        enableBusinessConversion,
        showConvertToBusinessCta,
        isBusinessSearchStep,
        onConvertToBusiness,
        onContinueBusinessConversion,
        onCancelBusinessConversion
    };
};

export default useBusinessConversion;
